import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";

/** Start Stripe Checkout for the caller. Returns false when billing isn't configured yet
 *  (server 503) or the call fails — callers fall back to the mailto path. */
export interface CurrentPrice { usd: number | null; seatsRemaining: number | null; listUsd: number | null }

/** The price a new subscriber pays right now (cohort ladder). Null = billing not configured. */
export async function fetchCurrentPrice(): Promise<CurrentPrice | null> {
  try {
    const token = await getIdToken();
    const r = await fetch(`${config.restUrl}/api/billing/checkout`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return (await r.json()) as CurrentPrice;
  } catch {
    return null;
  }
}

/** POST an authed billing endpoint and open the returned Stripe URL in a NEW tab, keeping the
 *  desk session alive in this one. The blank tab is opened SYNCHRONOUSLY (inside the click's
 *  user activation) so popup blockers allow it, then pointed at Stripe once the URL arrives.
 *  Popup blocked anyway → same-tab navigation. False = endpoint unavailable (caller falls back). */
async function openBillingUrl(path: string): Promise<boolean> {
  const win = window.open("", "_blank");
  try {
    const token = await getIdToken();
    const r = await fetch(`${config.restUrl}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const j = (await r.json().catch(() => ({}))) as { url?: string };
    if (r.ok && j.url) {
      if (win) win.location.href = j.url;
      else window.location.href = j.url;
      return true;
    }
  } catch {
    /* fall through */
  }
  win?.close();
  return false;
}

/** Open the Stripe customer portal (cancel / card / invoices) in a new tab. False = not available. */
export function openPortal(): Promise<boolean> {
  return openBillingUrl("/api/billing/portal");
}

/** Open Stripe Checkout in a new tab. False = billing not configured (caller falls back to mailto). */
export function startCheckout(): Promise<boolean> {
  return openBillingUrl("/api/billing/checkout");
}
